"use client";

import clsx from "clsx";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";

export type MarketingIndustryStat = {
  value: string;
  label: string;
};

export type MarketingIndustryCard = {
  id: string;
  category: string;
  headline: string;
  description: string;
  stats: MarketingIndustryStat[];
  logoSrc: string;
  logoAlt: string;
  logoWidth: number;
  logoHeight: number;
};

export type MarketingIndustrySectionProps = {
  title: string;
  subtitle: string;
  cards: MarketingIndustryCard[];
  className?: string;
};

function IndustryCard({
  card,
}: Readonly<{ card: MarketingIndustryCard }>) {
  return (
    <article className="flex h-full min-h-full flex-col rounded-3xl border border-black/10 bg-marketing-voice-card p-6 md:p-8">
      <p className="font-mono text-xs uppercase tracking-[0.14em] text-gail-primary">
        {card.category}
      </p>

      <h3 className="mt-4 border-b border-black/10 pb-6 font-monument text-[1.625rem] font-medium leading-snug text-black">
        {card.headline}
      </h3>

      <p className="mt-6 font-monument text-sm leading-relaxed text-black/70 md:text-base">
        {card.description}
      </p>

      <dl className="mt-auto pt-8">
        {card.stats.map((stat) => (
          <div
            key={stat.label}
            className="flex items-baseline justify-between gap-4 border-t border-black/10 py-3"
          >
            <dd className="shrink-0 font-monument text-2xl font-medium text-black md:text-3xl">
              {stat.value}
            </dd>
            <dt className="text-right font-monument text-sm leading-snug text-black/70">
              {stat.label}
            </dt>
          </div>
        ))}
      </dl>

      <div className="pt-8">
        <div className="flex h-8 w-28 items-end justify-start">
          <Image
            src={card.logoSrc}
            alt={card.logoAlt}
            width={card.logoWidth}
            height={card.logoHeight}
            className="max-h-full max-w-full object-contain object-left opacity-35 brightness-0"
          />
        </div>
      </div>
    </article>
  );
}

export function MarketingIndustrySection({
  title,
  subtitle,
  cards,
  className,
}: Readonly<MarketingIndustrySectionProps>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const updateActiveIndex = useCallback(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const children = Array.from(container.children) as HTMLElement[];
    if (children.length === 0) {
      return;
    }

    const containerCenter = container.scrollLeft + container.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    children.forEach((child, index) => {
      const childCenter = child.offsetLeft + child.offsetWidth / 2;
      const distance = Math.abs(childCenter - containerCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setActiveIndex(closestIndex);
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const raf = requestAnimationFrame(updateActiveIndex);
    container.addEventListener("scroll", updateActiveIndex, { passive: true });
    window.addEventListener("resize", updateActiveIndex);

    return () => {
      cancelAnimationFrame(raf);
      container.removeEventListener("scroll", updateActiveIndex);
      window.removeEventListener("resize", updateActiveIndex);
    };
  }, [updateActiveIndex]);

  const scrollToCard = (index: number) => {
    const container = scrollRef.current;
    const card = container?.children[index] as HTMLElement | undefined;
    if (!container || !card) {
      return;
    }

    container.scrollTo({
      left: card.offsetLeft - (container.clientWidth - card.offsetWidth) / 2,
      behavior: "smooth",
    });
  };

  return (
    <section className={clsx("w-full px-4 pb-16 md:pb-24", className)}>
      <div className="mx-auto w-full max-w-6xl">
        <div className="max-w-4xl">
          <h2 className="font-monument text-[2rem] font-medium leading-[1.12] tracking-tight text-black md:text-[2.5rem]">
            {title}
          </h2>
          <p className="mt-4 max-w-3xl font-monument text-xl leading-[1.2] tracking-tight text-black/70 md:text-[1.75rem]">
            {subtitle}
          </p>
        </div>

        <div
          ref={scrollRef}
          className="-mx-4 mt-10 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] md:mx-0 md:mt-12 md:grid md:grid-cols-3 md:gap-6 md:overflow-visible md:snap-none md:px-0 md:pb-0 [&::-webkit-scrollbar]:hidden"
        >
          {cards.map((card) => (
            <div
              key={card.id}
              className="w-[min(88vw,22rem)] shrink-0 snap-center md:w-auto md:shrink"
            >
              <IndustryCard card={card} />
            </div>
          ))}
        </div>

        <div
          className="mt-6 flex justify-center gap-2 md:hidden"
          aria-label="Industry cards"
        >
          {cards.map((card, index) => (
            <button
              key={card.id}
              type="button"
              aria-label={`Show ${card.category} card`}
              aria-current={index === activeIndex ? "true" : undefined}
              onClick={() => scrollToCard(index)}
              className={clsx(
                "h-1.5 rounded-full transition-all duration-200",
                index === activeIndex
                  ? "w-6 bg-black/70"
                  : "w-1.5 bg-black/20",
              )}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
